import { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { Clock, MapPin, Navigation } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

import { BACKEND_URL, API } from '../utils/apiConfig';

// Fix Leaflet marker icons (Wichtig, damit die Icons in React korrekt geladen werden)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons (Grün für Start, Rot für Ziel, Orange für Stopps)
const startIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const destIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const stopIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Helper-Komponente: Zoomt die Karte automatisch so, dass die gesamte Route sichtbar ist
function FitBounds({ bounds }) {
  const map = useMap();
  
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);
  
  return null;
}

export const RideMap = ({ startCity, destinationCity, intermediateStops = [], showETA = true, startCoords = null, endCoords = null, livePosition = null }) => {
  const [routeData, setRouteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  // Cleanup on unmount — kill all pending requests and timers
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Can route if we have coords OR city names (at least 2 chars)
    const hasCoords = startCoords?.lat && endCoords?.lat;
    const hasCities = startCity?.length >= 2 && destinationCity?.length >= 2;

    if (hasCoords || hasCities) {
      debounceRef.current = setTimeout(() => {
        if (mountedRef.current) loadRoute();
      }, hasCoords ? 200 : 800);
    } else {
      setRouteData(null);
      setError(null);
    }

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [startCity, destinationCity, startCoords?.lat, startCoords?.lng, endCoords?.lat, endCoords?.lng]); 

  const loadRoute = async () => {
    // Abort any previous request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    
    try {
      const payload = {
        start_city: startCity || '',
        destination_city: destinationCity || '',
        intermediate_stops: intermediateStops || []
      };

      // Pass coords directly when available (from MapPicker) — skips geocoding
      if (startCoords?.lat && startCoords?.lng) {
        payload.start_lat = startCoords.lat;
        payload.start_lng = startCoords.lng;
      }
      if (endCoords?.lat && endCoords?.lng) {
        payload.dest_lat = endCoords.lat;
        payload.dest_lng = endCoords.lng;
      }

      const response = await axios.post(`${API}/tracking/route`, payload, { signal: controller.signal });
      if (mountedRef.current) setRouteData(response.data);
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      console.error("Karten-Fehler:", err);
      if (mountedRef.current) setError(err.response?.data?.detail || 'Route konnte nicht geladen werden');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  // Default center (Deutschland)
  const defaultCenter = [51.1657, 10.4515];
  
  if (loading) {
    return (
      <div className="ride-map-container loading" data-testid="ride-map-loading">
        <div className="map-loading">
          <Navigation className="spinning" size={24} />
          <span>Route wird berechnet...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ride-map-container" data-testid="ride-map-unavailable" style={{
        height: '100%', minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1',
        flexDirection: 'column', gap: '0.5rem', color: '#94a3b8', padding: '1.5rem', textAlign: 'center'
      }}>
        <MapPin size={28} />
        <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Kartenvorschau nicht verfügbar</span>
        <span style={{ fontSize: '0.8rem' }}>{startCity} → {destinationCity}</span>
      </div>
    );
  }

  if (!routeData || !startCity || !destinationCity) {
    return (
      <div className="ride-map-container empty" data-testid="ride-map-empty">
        <div className="map-placeholder">
          <MapPin size={32} />
          <span>Start- und Zielstadt eingeben</span>
        </div>
      </div>
    );
  }

  // Filter out invalid points from polyline
  const validPolyline = (routeData.polyline || []).filter(
    p => Array.isArray(p) && p.length >= 2 && p[0] != null && p[1] != null
  );
  
  // Bounds for FitBounds
  const bounds = validPolyline.length > 0 
    ? validPolyline 
    : [[routeData.start.lat, routeData.start.lng], [routeData.destination.lat, routeData.destination.lng]];

  // Center berechnen (Mittelpunkt zwischen Start und Ziel)
  const center = [
    (routeData.start.lat + routeData.destination.lat) / 2,
    (routeData.start.lng + routeData.destination.lng) / 2
  ];

  return (
    <div className="ride-map-container" data-testid="ride-map">
      
      {/* Das Info-Panel nur zeigen, wenn routeData wirklich da ist */}
      {showETA && routeData && (
        <div className="route-info-panel">
          <div className="route-info-item">
            <Navigation size={18} />
            <span>{routeData.distance_km} km</span>
          </div>
          <div className="route-info-item">
            <Clock size={18} />
            <span>{routeData.eta_text}</span>
          </div>
        </div>
      )}
      
      <MapContainer
        center={center}
        zoom={7}
        style={{ height: '100%', width: '100%', minHeight: '300px' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <FitBounds bounds={bounds} />
        
        {/* Start Marker */}
        <Marker position={[routeData.start.lat, routeData.start.lng]} icon={startIcon}>
          <Popup>
            <strong>Start:</strong> {routeData.start.name}
          </Popup>
        </Marker>
        
        {/* Intermediate Stops */}
        {routeData.intermediate_stops && routeData.intermediate_stops.map((stop, idx) => (
          <Marker 
            key={idx}
            position={[stop.lat, stop.lng]} 
            icon={stopIcon}
          >
            <Popup>
              <strong>Zwischenstopp:</strong> {stop.name}
            </Popup>
          </Marker>
        ))}
        
        {/* Destination Marker */}
        <Marker position={[routeData.destination.lat, routeData.destination.lng]} icon={destIcon}>
          <Popup>
            <strong>Ziel:</strong> {routeData.destination.name}
          </Popup>
        </Marker>
        
        {/* Route Polyline */}
        {validPolyline.length > 0 && (
          <Polyline 
            positions={validPolyline} 
            color="#667eea" 
            weight={4} 
            opacity={0.8}
          />
        )}
      </MapContainer>
    </div>
  );
};

// Kompakte Vorschau-Komponente für Ride-Cards
export const RideMapPreview = ({ startCity, destinationCity }) => {
  const [eta, setEta] = useState(null);

  useEffect(() => {
    if (startCity && destinationCity) {
      loadETA();
    }
  }, [startCity, destinationCity]);

  const loadETA = async () => {
    try {
      const response = await axios.get(`${API}/tracking/eta`, {
        params: { from_city: startCity, to_city: destinationCity }
      });
      setEta(response.data);
    } catch (err) {
      // Silent fallback — just don't show ETA
      setEta(null);
    }
  };

  if (!eta) return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', color: '#94a3b8', fontSize: '0.8rem' }}>
      Routendaten laden...
    </div>
  );

  return (
    <div className="ride-eta-badge" data-testid="ride-eta">
      <Clock size={14} />
      <span>{eta.eta_text}</span>
      <span className="divider">•</span>
      <span>{eta.distance_km} km</span>
    </div>
  );
};

export default RideMap;